import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import localforage from "localforage";
import _ from "lodash";
import { createSelector } from "reselect";
import { client } from "../App";
import { ContractStatusCodeEnum } from "../helpers/enums";
import {
  CONDOMINIUM_ADMIN_INVOICES_LIST,
  CONTRACT_DETAIL,
  LIST_CONTRACTS_SMALL,
  MUNICIPAL_INVOICES_LIST
} from "../helpers/queries";
import { Bill } from "../models";
import { Contract, ContractDetailReq, InvoicesReq } from "../models/Contract";
import { hideLoading, showLoading } from "./app";

export const upaidBills = createSelector(
  (state: any) => state.contracts.bills,
  (bills) => bills.filter((bill: any) => !bill.isPaid)
);

interface ContractsState {
  list: Array<Contract>;
  current: any | null;
  currentContractSelected: any | null;
  currentClientCodeSelected: any | null;
  bills: any[];
  adminMunicipalBillsToShow: any[];
  totalPages: any | null;
  usages: any[];
  metre: any | null;
  reloadContract: boolean;
  telemeter: boolean | null;
  loadChart: boolean;
  serviceOnlineBill: any | null;
  serviceBankDomiciliation: any | null;
  showedAdminMunicipalModal: boolean;
  contractsSelected: any[];
}

const initialState: ContractsState = {
  list: [],
  current: null,
  currentContractSelected: null,
  currentClientCodeSelected: null,
  bills: [],
  adminMunicipalBillsToShow: [],
  totalPages: null,
  usages: [],
  metre: null,
  reloadContract: false,
  telemeter: null,
  loadChart: false,
  serviceOnlineBill: null,
  serviceBankDomiciliation: null,
  showedAdminMunicipalModal: false,
  contractsSelected: [],
};

export const contractSlice = createSlice({
  name: "contracts",
  initialState,
  reducers: {
    setReloadContract(
      state: ContractsState,
      action: PayloadAction<boolean>
    ): void {
      state.reloadContract = action.payload;
    },
    setContracts(
      state: ContractsState,
      action: PayloadAction<Array<Contract>>
    ): void {
      state.list = action.payload;
    },
    setCurrent(state: ContractsState, action: PayloadAction<string>): void {
      state.current = action.payload;
    },
    setCurrentContractSelected(
      state: ContractsState,
      action: PayloadAction<string>
    ): void {
      state.currentContractSelected = action.payload;
    },
    setCurrentClientCodeSelected(
      state: ContractsState,
      action: PayloadAction<string>
    ): void {
      state.currentClientCodeSelected = action.payload;
    },
    setContractBills(
      state: ContractsState,
      action: PayloadAction<Bill[]>
    ): void {
      state.bills = !action.payload ? [] : action.payload;
    },
    setAdminMunicipalBillsToShow(
      state: ContractsState,
      action: PayloadAction<any[]>
    ): void {
      state.adminMunicipalBillsToShow = !action.payload ? [] : action.payload;
    },
    setTotalPages(state: ContractsState, action: PayloadAction<any>): void {
      state.totalPages = action.payload;
    },
    setContractUsages(
      state: ContractsState,
      action: PayloadAction<any[]>
    ): void {
      state.usages = !action.payload ? [] : action.payload;
    },
    setMetre(state: ContractsState, action: PayloadAction<any[]>): void {
      state.metre = !action.payload ? null : action.payload;
    },
    setTelemeter(state: ContractsState, action: PayloadAction<any>): void {
      state.telemeter = action.payload;
    },
    unsetState(state: ContractsState): void {
      state.list = [];
      state.current = null;
      state.currentContractSelected = null;
      state.currentClientCodeSelected = null;
      state.adminMunicipalBillsToShow = [];
      state.totalPages = null;
      state.bills = [];
      state.usages = [];
      state.metre = null;
      state.telemeter = null;
    },
    setShouldLoad(state: ContractsState, action: PayloadAction<boolean>): void {
      state.loadChart = action.payload;
    },
    setServiceOnlineBill(
      state: ContractsState,
      action: PayloadAction<any>
    ): void {
      state.serviceOnlineBill = action.payload;
    },
    setServiceBankDomiciliation(
      state: ContractsState,
      action: PayloadAction<any>
    ): void {
      state.serviceBankDomiciliation = action.payload;
    },
    setAdminMunicipalModalShowed(
      state: ContractsState,
      action: PayloadAction<boolean>
    ): void {
      state.showedAdminMunicipalModal = action.payload;
    },
    setContractsSelectedState(
      state: ContractsState,
      action: PayloadAction<any[]>
    ): void {
      state.contractsSelected = action.payload;
    },
  },
});

export const {
  setContracts,
  setCurrent,
  setCurrentContractSelected,
  setCurrentClientCodeSelected,
  setContractBills,
  setAdminMunicipalBillsToShow,
  setTotalPages,
  setContractUsages,
  setMetre,
  setTelemeter,
  unsetState,
  setReloadContract,
  setShouldLoad,
  setServiceOnlineBill,
  setServiceBankDomiciliation,
  setAdminMunicipalModalShowed,
  setContractsSelectedState,
} = contractSlice.actions;
const setPersist = async (contractId: string) => {
  return await localforage.setItem("contract", contractId).then().catch();
};
const getPersist = async () => {
  return await localforage
    .getItem("contract")
    .then((v) => v)
    .catch();
};
export const setReloadContracts = (reload: boolean) => (dispatch: Dispatch) => {
  dispatch(setReloadContract(reload));
};
export const reloadContracts = () => (dispatch: Dispatch, getState: any) => {
  const { contracts } = getState();
  if (contracts.reloadContract) {
    dispatch<any>(setReloadContracts(false));
    dispatch<any>(loadContracts());
  }
};
export const setCurrentContract =
  (contract: any) => async (dispatch: Dispatch, getState: any) => {
    const state = getState();
    if (
      !state.contracts.current ||
      state.contracts.current.id !== contract.id
    ) {
      await setPersist(contract.id);
      dispatch<any>(setCurrent(contract));
      dispatch<any>(loadContractDetail());
    }
  };

export const setBillsToShowForAdminMunicipal =
  (filterData: any, whichPage: any) =>
  async (dispatch: Dispatch, getState: any) => {
    const state = getState();
    dispatch<any>(showLoading());
    if (
      state.auth.profile.role === "CONDOMINIUM_ADMIN" ||
      (state.auth.impersonationActive &&
        state.auth.profileImpersonate.role === "CONDOMINIUM_ADMIN")
    ) {
      client()
        .query({
          query: CONDOMINIUM_ADMIN_INVOICES_LIST,
          variables: {
            req: {
              ...filterData,
              recordsPerPage: 10,
              page: whichPage,
            },
          },
        })
        .then((data: any) => {
          if (data) {
            dispatch<any>(hideLoading());
            dispatch<any>(
              setAdminMunicipalBillsToShow(
                data.data.condominiumAdminInvoicesList.elements
              )
            );
            dispatch<any>(
              setTotalPages(
                data.data.condominiumAdminInvoicesList.pagination.totalPages
              )
            );
          }
        })
        .catch((err: any) => {
          dispatch<any>(hideLoading());
          console.log(err, "error");
        });
    } else {
      client()
        .query({
          query: MUNICIPAL_INVOICES_LIST,
          variables: {
            req: {
              ...filterData,
              recordsPerPage: 10,
              page: whichPage,
            },
          },
        })
        .then((data: any) => {
          if (data) {
            dispatch<any>(hideLoading());
            dispatch<any>(
              setAdminMunicipalBillsToShow(
                data.data.municipalInvoicesList.elements
              )
            );
            dispatch<any>(
              setTotalPages(
                data.data.municipalInvoicesList.pagination.totalPages
              )
            );
          }
        })
        .catch((err: any) => {
          dispatch<any>(hideLoading());
          console.log(err, "error");
        });
    }
  };

export const setCurrentSelectedContract =
  (contract: any) => async (dispatch: Dispatch, getState: any) => {
    const state = getState();
    if (
      !state.contracts.currentContractSelected ||
      state.contracts.currentContractSelected.id !== contract.id
    ) {
      dispatch<any>(setCurrentContractSelected(contract));
      dispatch<any>(setAdminMunicipalModalShowed(false));
    }
  };

export const setCurrentSelectedClientCode =
  (clientCode: any) => async (dispatch: Dispatch, getState: any) => {
    const state = getState();
    if (
      !state.contracts.currentClientCodeSelected ||
      state.contracts.currentClientCodeSelected.clientCode !==
        clientCode.clientCode
    ) {
      dispatch<any>(setCurrentClientCodeSelected(clientCode));
    }
  };

export const setLoadChart = (load: boolean) => async (dispatch: Dispatch) => {
  dispatch<any>(setShouldLoad(load));
};

export const setShowedAdminMunicipalModal =
  (showed: boolean) => async (dispatch: Dispatch) => {
    dispatch<any>(setAdminMunicipalModalShowed(showed));
  };
export const setContractsSelected =
  (contracts: any[]) => async (dispatch: Dispatch) => {
    dispatch<any>(setContractsSelectedState(contracts));
  };

export const loadContractDetail = () => (dispatch: Dispatch, getState: any) => {
  const state = getState();
  const { contracts } = state;
  const { current } = contracts;
  const { id } = current;

  dispatch<any>(showLoading());
  return client()
    .query({
      query: CONTRACT_DETAIL,
      variables: { ...ContractDetailReq(id), ...InvoicesReq(false) },
    })
    .then((response: any) => {
      const { data } = response;
      const { contractDetail } = data;
      const { invoices } = contractDetail;
      const { elements: list } = invoices;
      const { metre } = contractDetail;
      const { telemeter } = contractDetail;
      const { serviceBankDomiciliation } = contractDetail;
      const { serviceOnlineBill } = contractDetail;
      const {
        consumptions: { elements },
      } = metre;
      const { diameter, indicesNumber, location, serialNumber } = metre;
      let clean = _.partial(_.omit, _, ["__typename"]);
      return {
        bills: _.map(list, clean),
        usages: _.map(elements, clean),
        metre: { diameter, indicesNumber, location, serialNumber },
        telemeter: telemeter,
        serviceBankDomiciliation: serviceBankDomiciliation,
        serviceOnlineBill: serviceOnlineBill,
      };
    })
    .then(
      ({
        bills,
        usages,
        metre,
        telemeter,
        serviceOnlineBill,
        serviceBankDomiciliation,
      }: {
        bills: any;
        usages: any;
        metre: any;
        telemeter: any;
        serviceOnlineBill: any;
        serviceBankDomiciliation: any;
      }) => {
        dispatch<any>(setServiceOnlineBill(serviceOnlineBill));
        dispatch<any>(setServiceBankDomiciliation(serviceBankDomiciliation));
        dispatch<any>(setContractBills(bills));
        dispatch<any>(setContractUsages(usages));
        dispatch<any>(setMetre(metre));
        dispatch<any>(setTelemeter(telemeter));
        dispatch<any>(hideLoading());
      }
    )
    .catch((error: any) => {
      dispatch<any>(setContractBills([]));
      dispatch<any>(setContractUsages([]));
      dispatch<any>(hideLoading());
    });
};

export const loadContracts =
  () => async (dispatch: Dispatch, getState: any) => {
    let id = await getPersist();
    return client()
      .query({
        query: LIST_CONTRACTS_SMALL,
      })
      .then((response: any) => {
        const { elements = [] } = response.data.listContracts;
        const data =
          elements === null
            ? []
            : elements.reduce((acc: any, curr: any) => {
                const {
                  puf: { street, streetNumber, city, province, postalCode },
                  contractId,
                  integraClientCode,
                  integraContractCode,
                  status: { code },
                  accountHolder,
                } = curr;
                acc.push({
                  id: contractId,
                  name: `${integraClientCode || ""} - ${
                    integraContractCode || ""
                  } ${street || ""} ${streetNumber || ""} ${city || ""} ${
                    province || ""
                  } ${postalCode || ""}`,
                  state:
                    ContractStatusCodeEnum[
                      code as keyof typeof ContractStatusCodeEnum
                    ] || "no_exist",
                  clientId: integraContractCode,
                  clientCode: integraClientCode,
                  street,
                  streetNumber,
                  city,
                  province,
                  postalCode,
                  accountHolder,
                });
                return acc;
              }, []);
        dispatch(setContracts(data));
        if (id) {
          let [contractPersist] = data.filter((i: any) => {
            return i.id === id;
          });
          if (contractPersist) {
            return dispatch<any>(setCurrentContract(contractPersist));
          }
        }
        if (data.length > 0) {
          const [firstContract] = data;
          return dispatch<any>(setCurrentContract(firstContract));
        }
      })
      .catch((error: any) => {
        console.error(error);
      });
  };

export const clearStateContract = () => (dispatch: Dispatch) => {
  dispatch(unsetState());
};

export default contractSlice.reducer;
